import { useId, type JSX, type Ref } from "react";
import { BULK_ACTIONS, BULK_MAX_LEADS, type BulkAction } from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import { Button } from "../../design/components/Button";
import { Icon, type IconName } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { formatCount } from "../../i18n/format";
import { useLocale, useT } from "../../i18n/locale";
import { allResultsText, selectAllText } from "./bulk-text";
import "./bulk.css";

// La barra delle «Azioni multiple» (piano M3 Task 14; mock-up `lead.html`, `renderBulkBar`): sotto i filtri e sopra la
// tabella, finché le azioni multiple sono accese. Dice quanti Lead sono scelti (lo annuncia anche a chi usa uno screen
// reader), offre «Seleziona tutti i N risultati» e «Deseleziona», e le azioni. Ogni azione segue la funzione della sua
// scrittura singola (D10): senza, è spenta con il motivo scritto (`Reason`), come senza Lead spuntati o con troppi Lead
// per una volta sola. Il server controlla comunque tutto. Il riepilogo riceve il focus quando il pulsante appena premuto
// sparisce o si spegne («Seleziona tutti i N risultati», «Deseleziona», un'azione confermata): mai sulla pagina
// (UX_SYSTEM, «Focus»).

/** Le funzioni che servono alle azioni, oltre a `bulk_actions`: «Cambia status» vuole soltanto il permesso di modifica. */
export type BulkFeature = "change_owner" | "tags" | "archive";

const FEATURE_OF: Readonly<Record<BulkAction, BulkFeature | null>> = {
  change_owner: "change_owner",
  change_status: null,
  add_tag: "tags",
  remove_tag: "tags",
  archive: "archive",
};

const ICON_OF: Readonly<Record<BulkAction, IconName>> = { change_owner: "user", change_status: "dot", add_tag: "tag", remove_tag: "tag", archive: "trash" };

const FEATURE_REASON: Readonly<Record<BulkFeature, MessageKey>> = {
  change_owner: "bulkActions.needsFeature.change_owner",
  tags: "bulkActions.needsFeature.tags",
  archive: "bulkActions.needsFeature.archive",
};

type Props = {
  /** «Seleziona tutti i N risultati» è attivo. */
  all: boolean;
  /** I Lead scelti: quelli spuntati, oppure con `all` tutti i risultati. */
  count: number;
  /** I risultati della vista; `null` finché non si conoscono. */
  total: number | null;
  /** Le funzioni che chi guarda ha. */
  features: Readonly<Record<BulkFeature, boolean>>;
  onSelectAll: () => void;
  onClear: () => void;
  onAction: (action: BulkAction) => void;
  /** Il riepilogo, dove la pagina porta il focus. */
  summaryRef?: Ref<HTMLParagraphElement>;
  /** La scelta aspetta le pagine di un elenco nuovo (`pending` di `useBulkSelection`): le azioni sono spente. */
  pending?: boolean;
};

export function BulkBar({ all, count, total, features, onSelectAll, onClear, onAction, summaryRef, pending = false }: Props): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const baseId = useId();
  const pickFirstId = `${baseId}-pick`;
  const tooLargeId = `${baseId}-large`;
  const waitId = `${baseId}-wait`;
  const featureId = (feature: BulkFeature) => `${baseId}-${feature}`;
  const empty = count === 0;
  const tooLarge = all && count > BULK_MAX_LEADS;
  const missing = (Object.keys(FEATURE_REASON) as BulkFeature[]).filter((feature) => !features[feature]);
  const countText = count === 1 ? t("bulkActions.selectedOne") : t("bulkActions.selectedMany", { count: formatCount(locale, count) });

  return (
    <div className="g-bulk" role="region" aria-label={t("bulkActions.button")}>
      <p ref={summaryRef} className="g-bulk__summary" tabIndex={-1}>
        <strong className="g-bulk__count" aria-live="polite" aria-atomic="true">
          {countText}
        </strong>
        {all ? <span className="g-bulk__all">{allResultsText(t, locale, count)}</span> : null}
      </p>
      {!all && total !== null && total > 0 ? <Button onClick={onSelectAll}>{selectAllText(t, locale, total)}</Button> : null}
      {empty ? null : <Button onClick={onClear}>{t("bulkActions.deselect")}</Button>}
      <span className="g-bulk__spacer" />
      <div className="g-bulk__actions">
        {BULK_ACTIONS.map((action) => {
          const feature = FEATURE_OF[action];
          const allowed = feature === null || features[feature];
          const describedBy = [empty ? pickFirstId : null, pending ? waitId : null, tooLarge ? tooLargeId : null, allowed || feature === null ? null : featureId(feature)]
            .filter(Boolean)
            .join(" ");
          return (
            <Button
              key={action}
              aria-haspopup="dialog"
              disabled={empty || pending || tooLarge || !allowed}
              aria-describedby={describedBy || undefined}
              onClick={() => onAction(action)}
            >
              <Icon name={ICON_OF[action]} />
              {t(`bulkActions.actions.${action}`)}
            </Button>
          );
        })}
      </div>
      {empty ? (
        <Reason id={pickFirstId} className="g-bulk__reason">
          {t("bulkActions.pickFirst")}
        </Reason>
      ) : null}
      {pending && !empty ? (
        <Reason id={waitId} className="g-bulk__reason">
          {t("bulkActions.waitList")}
        </Reason>
      ) : null}
      {tooLarge ? (
        <Reason id={tooLargeId} tone="warning" className="g-bulk__reason">
          {t("bulkActions.tooLarge", { count: formatCount(locale, count) })}
        </Reason>
      ) : null}
      {missing.map((feature) => (
        <Reason key={feature} id={featureId(feature)} className="g-bulk__reason">
          {t(FEATURE_REASON[feature])}
        </Reason>
      ))}
    </div>
  );
}
